
import Anchor from "../../../../../shared/lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutEvent from "../../../../../shared/lib/html5-software-library/src/ranj/layout/LayoutEvent";
import AnimationFactory from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import { isNull } from "../../../../../shared/lib/html5-software-library/src/ranj/util/TypeUtil";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import SkinKeys from "../../../../../shared/src/ranj/app/ui/SkinKeys";
import SaveEvent from "../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import InteractionEventName from "../data/InteractionEventName";
import GameConfig from "../model/GameConfig";
import GameModel from "../model/GameModel";
import GameStatus from "../model/GameStatus";
import Navigation from "../model/Navigation";
import SaveModel from "../model/SaveModel";
import AbstractWorkingMemoryScreenMediator from "./AbstractWorkingMemoryScreenMediator";
import BottomHudView from "./BottomHudView";
import GameElementSkinFactory from "./GameElementSkinFactory";
import HudView from "./HudView";
import PopupView from "./PopupView";
import PuzzleView from "./PuzzleView";

export default class GameScreenMediator extends AbstractWorkingMemoryScreenMediator {

    /**
     * @param {*} target 
     * @param {*} dispatcher 
     * @param {*} languagePack 
     * @param {*} layoutManager 
     * @param {*} soundManager 
     * @param {GameModel} gameModel 
     * @param {GameConfig} gameConfig 
     * @param {RoadBuilder} roadBuilder 
     * @param {Object} roadAssets 
     * @param {SaveModel} saveModel 
     */
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, gameConfig, saveModel, gameAssets, gameElementSkinFactory) {
        super(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, saveModel, gameAssets);
        this._gameConfig = gameConfig;
        this._elementFactory = gameElementSkinFactory;

        this._handleTick = this._handleTick.bind(this);
        this._handleGameModelStatus = this._handleGameModelStatus.bind(this);
        this._handleGameModelPuzzle = this._handleGameModelPuzzle.bind(this);
        this._handleGameModelPercentTimer = this._handleGameModelPercentTimer.bind(this);
        this._handleGameModelTimeout = this._handleGameModelTimeout.bind(this);
        this._handlePuzzleViewItemSelect = this._handlePuzzleViewItemSelect.bind(this);
        this._handleLayoutChanged = this._handleLayoutChanged.bind(this);
        this._handleTutorialSeen = this._handleTutorialSeen.bind(this);

        this._currentPuzzleSuccessful = null;
    }

    initialize() {
        super.initialize();

        this._stage = this._target.stage;

        this.anchorObject(this._target.hud, Anchor.T);
        this.anchorObject(this._target.hudBottom, Anchor.B);
        this.anchorObject(this._target.level, Anchor.C);

        this._levelPlaceholder = this._target.level;
        this._levelPlaceholder.removeAllChildren();

        this._hud = new HudView(this._target.hud, this._languagePack);
        this._hudBottom = new BottomHudView(this._target.hudBottom, this._languagePack);
        this._hudBottom.enableSubmit(false);
        this._hudBottom.enableClear(false);

        this._hudBottom.addEventListener(BottomHudView.SUBMIT, this._handleSubmitClick.bind(this));
        this._hudBottom.addEventListener(BottomHudView.CLEAR, this._handleClearClick.bind(this));
        this._hudBottom.addEventListener(BottomHudView.NEXT, this._handleNextClick.bind(this));

        this._initializeDebug();

        this._addEventListeners();

        if (!isNull(this._saveModel.getSaveGame())) {
            this._resumeGameAfterRestore();
        } else {
            this._startTutorial();
        }
    }

    destroy() {
        super.destroy();
        this._stopTicker();
        this._removeEventListeners();
        this._hud.destroy();
        this._hudBottom.destroy();
        this._destroyPuzzleView(this._puzzleView);
        if (window.gamescreen == this) {
            window.gamescreen = null;
        }
    }

    _addEventListeners() {
        this._gameModel.addEventListener(GameModel.STATUS, this._handleGameModelStatus);
        this._gameModel.addEventListener(GameModel.PUZZLE, this._handleGameModelPuzzle);
        this._gameModel.addEventListener(GameModel.PERCENT_TIMER, this._handleGameModelPercentTimer);
        this._gameModel.addEventListener(GameModel.TIMEOUT, this._handleGameModelTimeout);
        this.registerEventListener(GameModel.TUTORIAL_SEEN, this._handleTutorialSeen.bind(this));
        this.registerEventListener(LayoutEvent.CHANGED, this._handleLayoutChanged);
    }
    _removeEventListeners() {
        this._gameModel.removeEventListener(GameModel.STATUS, this._handleGameModelStatus);
        this._gameModel.removeEventListener(GameModel.PUZZLE, this._handleGameModelPuzzle);
        this._gameModel.removeEventListener(GameModel.PERCENT_TIMER, this._handleGameModelPercentTimer);
        this._gameModel.removeEventListener(GameModel.TIMEOUT, this._handleGameModelTimeout);
        this.unregisterEventListener(GameModel.TUTORIAL_SEEN, this._handleTutorialSeen.bind(this));
    }

    _handleLayoutChanged() {
        this._updateViewport();
    }

    _getViewport() {
        const viewport = this._layoutManager.getViewport(this._target);//in stage coordspace     
        //Correct for position of puzzle view and space covered by huds   
        this._target.localToLocalRect(viewport, this._levelPlaceholder, viewport);
        viewport.y = 0;
        viewport.height -= 348;//Hardcoded...
        return viewport;
    }

    _updateViewport() {
        if (this._puzzleView) {
            this._puzzleView.viewport = this._getViewport();
        }
    }

    _handleGameModelStatus({ data: status }) {
    }

    _startTicker() {
        if (!this._tickerStarted) {
            this._tickTime = performance.now();
            this._stage.addEventListener("tick", this._handleTick);
            this._tickerStarted = true;
        }
    }
    _stopTicker() {
        this._tickerStarted = false;
        this._stage.removeEventListener("tick", this._handleTick);
    }
    _handleTick() {
        if (!this._paused) {
            const now = performance.now();
            const delta = now - this._tickTime;
            this._tickTime = now;
            this._gameModel.step(delta);
        }
    }

    // ENTRY POINTS 

    _startPractice() {
        this._hud.showPracticeIndicator(true);
        this._gameModel.startPractice();
        //Force model to update some values
        this._gameModel.step(0);
        //some seconds delay for level transition which can be laggy on low-performing devices
        this.callDelayed(this._startTicker.bind(this), 3000);
    }

    _startGame() {
        this._hud.showPracticeIndicator(false);
        this._gameModel.start();
        //Force model to update some values
        this._gameModel.step(0);
        //some seconds delay for level transition which can be laggy on low-performing devices
        this.callDelayed(this._startTicker.bind(this), 3000);
    }

    _resumeGameAfterRestore() {
        this._gameModel.resumeAfterRestore();
        this._hud.showPracticeIndicator(this._gameModel.status == GameStatus.PRACTICE);
        const delay = this.checkSavegameRestoredFeedback(500) ? 1500 : 1000;
        this.callDelayed(this._startTicker.bind(this), delay);
    }

    //

    _doNextPuzzle() {
        this._gameModel.doNextPuzzle();
    }

    _handleGameModelPuzzle() {
        this._doPuzzleTransition();
    }

    _handleGameModelPercentTimer(evt) {
        this._hudBottom.setTimer(evt.data);
    }

    _handleGameModelTimeout() {
        this._handleSubmittingPuzzle(true);
    }

    _doPuzzleTransition() {
        const puzzleModel = this._gameModel.puzzle;
        this._hudBottom.showNext(false);
        this._hudBottom.showSubmit(false);
        this._hudBottom.enableSubmit(false);
        this._hudBottom.showClear(false);
        this._hudBottom.enableClear(false);

        const createNew = _ => {
            const target = this._levelPlaceholder.addChild(new createjs.Container());
            this._puzzleView = new PuzzleView(target, puzzleModel, this._elementFactory, this._getViewport());
            this._puzzleView.enabled = false;
            target.alpha = 0;
        }

        const animateIn = (duration, delay, fade = false) => {
            this._puzzleView.animateIn(duration, delay, onAnimateInComplete, fade);
        }

        const onAnimateInComplete = _ => {
            if (ranj.DEBUG && getURLParameter("skipintro") == "1") {
                onLevelIntroClose();
            } else {
                const options = new AnimationOptions(0, 500, createjs.Ease.quadIn, []);
                this.openPopup(SkinKeys.LEVEL_PREPARE_OVERLAY, onLevelIntroClose, AnimationFactory.FADE_IN, options);
            }
        }

        const onLevelIntroClose = _ => {
            const speedFactor = this._gameModel.speedFactor;

            this.callDelayed(_ => {
                this._puzzleView.playSequence(
                    onSequencePlayed,
                    this._gameConfig.getValue("timings.durationAnimation") / speedFactor,
                    this._gameConfig.getValue("timings.durationFadeInFish") / speedFactor,
                    this._gameConfig.getValue("timings.durationFadeOutFish") / speedFactor,
                    this._gameConfig.getValue("timings.delayBetweenAnimations") / speedFactor,
                    this._gameConfig.getValue("timings.delayBeforeAnswering") / speedFactor
                );
            }, this._gameConfig.getValue("timings.delayBeforeObserving") / speedFactor);
        }

        const onSequencePlayed = _ => {
            this._gameModel.startPuzzleInteraction();
            this._hudBottom.enableClear(false);
            this._hudBottom.enableSubmit(false);
            this._puzzleView.addEventListener(PuzzleView.SELECT, this._handlePuzzleViewItemSelect);

            var sampleFishVariation = puzzleModel.sampleFishVariation;
            var currentRuleID = puzzleModel.ruleId;
            this._puzzleView.setRule(this.getText("game.rules." + currentRuleID), sampleFishVariation != null);

            this.callDelayed(_ => {
                this._hudBottom.showTimer();
                this._hudBottom.showSubmit(true);
                this._hudBottom.showClear(true);
            }, 1450);

            this.callDelayed(_ => {
                this._puzzleView.enable();
                this._gameModel.startTimer();
            }, 1700);

            if (sampleFishVariation) {
                const sampleSkin = this._elementFactory.getItem(sampleFishVariation);
                this._puzzleView.setSample(sampleSkin);
            } else {
                this._puzzleView.setSample(null);
            }
        }

        if (this._puzzleView) {
            var oldPuzzleView = this._puzzleView;
            if (this._gameConfig.getValue("pondTransitionSlidingUpAnimation") && this._gameModel.isPreviousPondDifferent()) {
                // If we change pond, we are sliding-in the new pond
                oldPuzzleView.disable();
                oldPuzzleView.animateOut(2000, 0, _ => {
                    this._destroyPuzzleView(oldPuzzleView);
                });
                createNew();
                animateIn(2000, 0);
            } else {
                // if we do not change pond, we are fading-in the new pond
                this._puzzleView.disable();
                this._puzzleView.animateOut(300, 0, _ => {
                    this._destroyPuzzleView(this._puzzleView);
                    this.callDelayed(createNew, 50);
                    this.callDelayed(_ => animateIn(300, 700, true), 150);
                }, true);
            }


        } else {
            createNew();
            animateIn(300, 0, true);
        }
    }

    /**
     * @param {PuzzleView} puzzleView 
     */
    _destroyPuzzleView(puzzleView = null) {
        if (puzzleView) {
            this._levelPlaceholder.removeChild(puzzleView.target);
            puzzleView.destroy();
        }
    }

    _handleSubmittingPuzzle(timeout = false) {
        this._puzzleView.setRule("");
        this._puzzleView.disable();
        this._hudBottom.hideTimer();
        this._hudBottom.enableClear(false);
        this._hudBottom.enableSubmit(false);
        this._hudBottom.enableNext(false);
        this._hudBottom.showClear(false, false, 200);
        this._hudBottom.showSubmit(false, false, 200);

        this._currentPuzzleSuccessful = this._submitPuzzle(timeout);
        this._gameModel.stopTimer();
        //TODO: this is rather messy code. Timeout should be a handled as a separate case in the if-else block
        let timeoutText = this.getText("game.level.timeout");

        let text;
        if (!this._currentPuzzleSuccessful) {
            if (this._gameModel.isPuzzleAllowFail()) {
                text = this.getText("game.level.failure");
            } else {
                if (this._gameModel.canRetryPuzzle()) {
                    text = this.getText("game.level.failureRetry");
                    timeoutText = this.getText("game.level.timeoutRetry");
                } else {
                    text = this.getText("game.level.failureFinalRetry");
                    timeoutText = this.getText("game.level.timeOutFinalRetry");
                }
            }
        } else {
            const feedbackId = this._gameModel.puzzle.successFeedback || 1;
            text = this.getText(`game.level.success.${feedbackId}`);
        }

        text = (timeout ? timeoutText : text);

        this.callDelayed(_ => {
            this._puzzleView.setSample(null);
            this._puzzleView.showFeedback(text, this._currentPuzzleSuccessful);
            this._hudBottom.showNext(true, false, 1000);
            this._hudBottom.enableNext(true);
        }, 200);
    }


    /*******************************
     *     PUZZLE INTERACTION
     *******************************/

    _handleSubmitClick() {
        this._handleSubmittingPuzzle(false);
    }

    _handleClearClick() {
        const lastSelection = this._gameModel.puzzle.lastPickedSpot;
        if (lastSelection != null) {
            lastSelection.button.setSelected(false);
            lastSelection.button.setEnabled(true);
        }

        this._gameModel.deselectItem(lastSelection);

        this._hudBottom.enableClear(this._gameModel.puzzle.lastPickedSpot != null);
        this._hudBottom.enableSubmit(this._gameModel.puzzle.lastPickedSpot != null);
    }

    _handleNextClick() {
        this._hudBottom.showNext(false, false, 300);
        this._hudBottom.enableNext(false);
        //delay for nicer feel
        this.callDelayed(() => {
            this._finishPuzzle();
        }, 300);
    }

    _goToEndscreen() {
        this._stopTicker();
        this.gotoScreen(SkinKeys.END_SCREEN);
    }

    _submitPuzzle(timeout = false) {
        return this._gameModel.submitPuzzle(timeout);
    }

    _finishPuzzle() {
        if (this._gameModel.status == GameStatus.PRACTICE) {
            if (this._gameModel.hasNextPracticePuzzle()) {
                this._doNextPuzzle();
            } else {
                this._startGateKeeper();
            }
        } else {
            if (!this._currentPuzzleSuccessful && !this._gameModel.isPuzzleAllowFail()) {
                //Failed and not allowed to fail: retry if possible
                //TECH DEBT: IMO this is rarther messy or confusing. Not allowed to fail, so retry?? 
                if (!this._gameModel.retryPuzzle()) {
                    this._gameModel.end();
                    this._goToEndscreen();
                }
            } else {
                //Succeeded or allowed to fail: go to next if any
                if (this._gameModel.hasNextPuzzle()) {
                    this._doNextPuzzle();
                } else {
                    this._gameModel.end();
                    this._goToEndscreen();
                }
            }
        }
    }

    _handlePuzzleViewItemSelect({ data: slotIndex }) {
        this._hudBottom.enableSubmit(true);
        this._hudBottom.enableClear(true);
        this._gameModel.selectItem(slotIndex);
    }


    /********************
     *     TUTORIAL
     ********************/

    _startTutorial(again = false) {
        if (getURLParameter("skiptutorial") != "1") {
            const delay = again ? 0 : this._gameConfig.getValue("tutorial.delay");
            const duration = this._gameConfig.getValue("tutorial.fadeDuration");
            const options = new AnimationOptions(delay, duration, createjs.Ease.quadIn);
            this.openPopup(SkinKeys.TUTORIAL_OVERLAY, null, AnimationFactory.FADE_IN, options);
        } else {
            this._startPractice();
        }
    }

    _handleTutorialSeen() {
        if (!this._gameModel.tutorialAlreadySeen) {
            this._gameModel.tutorialAlreadySeen = true;
            this.dispatch(GameModel.CLOSE_TUTORIAL_POPUP);
            this.callDelayed(this._startPractice.bind(this), 1000);
        } else {
            const options = new AnimationOptions(0, 500, createjs.Ease.quadIn, []);
            this.openPopup(SkinKeys.GENERIC_POPUP, this._onTutorialSeenPopupClose.bind(this), AnimationFactory.FADE_IN, options);
        }
    }

    _onTutorialSeenPopupClose(navType) {
        this.dispatch(GameModel.CLOSE_TUTORIAL_POPUP);
        if (navType == Navigation.BACK) {
            this.callDelayed(this._startPractice.bind(this), 1000);
        } else {
            this.callDelayed(this._startGame.bind(this), 1000);
        }
    }

    _startGateKeeper() {
        const delay = this._gameConfig.getValue("gatekeeper.delay");
        const duration = this._gameConfig.getValue("gatekeeper.fadeDuration");
        const callback = _ => {
            this._puzzleView.enabled = false;
            this._hud.showPracticeIndicator(false);
        }
        const options = new AnimationOptions(delay, duration, createjs.Ease.quadIn, [callback]);
        this.openPopup(SkinKeys.GATEKEEPER_OVERLAY, this._handleGateKeeperClose.bind(this), AnimationFactory.FADE_IN, options);
    }

    _handleGateKeeperClose(navType) {
        this._hudBottom.enableSubmit(false);
        this._hudBottom.enableClear(false);
        if (navType == Navigation.BACK) {
            this._startTutorial();
        } else {
            this.callDelayed(this._startGame.bind(this), 1000);
        }
    }

    /********************
     *     DEBUG
     ********************/

    _initializeDebug() {
        if (ranj.DEBUG) {
            const doPuzzle = idx => {
                abortLevel();
                this._gameModel._puzzleIndex = idx - 1;
                this._doNextPuzzle();
            }
            const abortLevel = () => {
                createjs.Tween.removeAllTweens();
                ranj.app.injector.getValue("screenNavigator").closeAllPopUps();
                this._gameModel.stopTimer();
                this._hudBottom.hideTimer();
            }
            let solutionShown = false;
            const toggleSolutionInfo = () => {
                solutionShown = !solutionShown;
                this._puzzleView.showSolutionInfo(solutionShown);
            }

            window.document.addEventListener('keyup', e => {
                const c = String.fromCharCode(e.which);
                //console.log(c, e.code, e.which);
                if (c == "E") {
                    this._dispatcher.dispatch(SaveEvent.CLEAR);
                    this._goToEndscreen();
                } else if (c == "L") {
                    doPuzzle(this._gameConfig.puzzles.length - 1);
                } else if (c == "F") {
                    this._startGame();
                } else if (c == "C") { //reset puzzle
                    doPuzzle(this._gameModel._puzzleIndex);
                } else if (c == "S") { //reset puzzle
                    toggleSolutionInfo();
                } else if (e.which == 37) { // Arrow left: prev puzzle
                    doPuzzle(Math.max(0, this._gameModel._puzzleIndex - 1));
                } else if (e.which == 39) { // Arrow right: next puzzle
                    abortLevel();
                    this._currentPuzzleSuccessful = true;
                    this._gameModel.lastPuzzleSuccessful = true;
                    this._finishPuzzle();
                } else {
                    const n = parseInt(c);
                    if (!isNaN(n)) {
                        if (this._debugLevelNr == null) this._debugLevelNr = "";
                        this._debugLevelNr += n;
                        if (this._timeoutDebugLevel != null) {
                            clearTimeout(this._timeoutDebugLevel);
                            this._timeoutDebugLevel = null;
                        }
                        const debugLevelIdx = parseInt(this._debugLevelNr) - 1;
                        if (debugLevelIdx >= 0 && debugLevelIdx < this._gameConfig.puzzles.length) {
                            const dur = debugLevelIdx > 1 ? 0 : 300;
                            this._timeoutDebugLevel = setTimeout(() => {
                                this._debugLevelNr = "";
                                doPuzzle(debugLevelIdx);
                            }, dur);
                        } else {
                            this._debugLevelNr = "";
                        }
                    }
                }
            });
            window.gamescreen = this;//for console access
        }
    }
}